import { useEffect, useState } from 'react';
import { X, ChevronLeft, Minus, Plus, Star, BookOpen } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { useSocial } from '../hooks/useSocial';
import { getVariantConfig } from '../utils/cardVariants';
import { getTradeableQty, overrideKey } from '../utils/tradeables';
import { formatCardNumber } from '../utils/setMerges';
import { NATIONAL_DEX, MAX_DEX } from '../data/nationalDex';

const padDex = (num) => `#${String(num).padStart(3, '0')}`;

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjEwMCIgY3k9IjE0MCIgcj0iNDAiIGZpbGw9IiNEMUQ1REIiLz4KPC9zdmc+';

// Falls large -> small -> a static placeholder, then stops — without the
// stop, a set with no images at all (e.g. a newly-added local set whose
// source hasn't uploaded assets yet) makes small fail too and loops onError
// forever reassigning the same broken src.
const handleImgError = (e, card) => {
  const stage = e.target.dataset.fallbackStage;
  if (!stage && card.images.small && card.images.small !== card.images.large) {
    e.target.dataset.fallbackStage = 'small';
    e.target.src = card.images.small;
  } else {
    e.target.onerror = null;
    e.target.dataset.fallbackStage = 'placeholder';
    e.target.src = FALLBACK_IMG;
  }
};

const PRIORITY_OPTIONS = [
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Med' },
  { key: 'low', label: 'Low' }
];

const CardModal = ({ card, onClose }) => {
  const {
    updateCardQuantity, getVariantQuantity, getTotalQuantity, settings,
    addToPokedex, getPokedexSlot
  } = useCollection();
  const {
    isWishlisted, getWishlistItem, addToWishlist, removeFromWishlist, setWishlistPriority,
    tradeOverrides, setTradeOverride, clearTradeOverride, openProposalBuilder
  } = useSocial();
  const [comingSoonMsg, setComingSoonMsg] = useState('');
  const [binderUI, setBinderUI] = useState('idle'); // 'idle' | 'confirm' | 'added'

  const toggleVariantWishlist = (variant) => {
    if (isWishlisted(card.id, variant)) {
      removeFromWishlist(card.id, variant);
    } else {
      addToWishlist(card, variant, 'medium');
    }
  };

  const tradeThreshold = Number.isInteger(settings.tradeThreshold) ? settings.tradeThreshold : 1;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!card) return null;

  const totalOwned = getTotalQuantity(card.id);
  const variants = card.variants || [];
  const displayNumber = formatCardNumber(card);

  const incrementQuantity = (variant) => {
    const qty = getVariantQuantity(card.id, variant);
    updateCardQuantity(card.id, variant, qty + 1);
  };

  const decrementQuantity = (variant) => {
    const qty = getVariantQuantity(card.id, variant);
    if (qty > 0) updateCardQuantity(card.id, variant, qty - 1);
  };

  // First variant of this card with tradeable copies — seeds the proposal
  // builder from "Offer Trade"
  const firstTradeableVariant = variants.find((variant) => {
    const qty = getVariantQuantity(card.id, variant);
    const override = tradeOverrides[overrideKey(card.id, variant)];
    return getTradeableQty(qty, override, tradeThreshold) > 0;
  });

  const handleOfferTrade = () => {
    if (!firstTradeableVariant) {
      setComingSoonMsg('No spare copies to trade — add duplicates first.');
      setTimeout(() => setComingSoonMsg(''), 2500);
      return;
    }
    onClose();
    openProposalBuilder({ item: { cardId: card.id, variant: firstTradeableVariant } });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Pokédex binder — only Pokémon cards with a resolvable dex number get
  // the button. Multi-number cards (fusions) use the first number.
  const dexNumber =
    card.supertype === 'Pokémon' ? card.nationalPokedexNumbers?.[0] : undefined;
  const binderEligible = !!dexNumber && dexNumber <= MAX_DEX;
  const existingSlot = binderEligible ? getPokedexSlot(dexNumber) : null;
  const isBinderCard = existingSlot?.cardId === card.id;

  const handleAddToPokedex = () => {
    if (existingSlot && !isBinderCard) {
      setBinderUI('confirm');
      return;
    }
    addToPokedex(card);
    setBinderUI('added');
  };

  const handleConfirmReplace = () => {
    addToPokedex(card);
    setBinderUI('added');
  };

  const metaChips = (
    <div className="flex flex-wrap gap-2">
      {card.rarity && (
        <span className="text-[11px] font-medium bg-cream-bg border border-border text-ink-secondary px-2.5 py-1 rounded-full">
          {card.rarity}
        </span>
      )}
      {card.hp && (
        <span className="text-[11px] font-medium bg-danger-bg border border-danger-border text-brick px-2.5 py-1 rounded-full">
          HP {card.hp}
        </span>
      )}
      {card.types && card.types.length > 0 && (
        <span className="text-[11px] font-medium bg-cream-bg border border-border text-ink-secondary px-2.5 py-1 rounded-full">
          {card.types.join(', ')}
        </span>
      )}
      <span className="text-[11px] font-medium bg-cream-bg border border-border text-ink-secondary px-2.5 py-1 rounded-full">
        {displayNumber}
      </span>
    </div>
  );

  const ownedBadge = totalOwned > 0 && (
    <div className="px-3 py-2.5 bg-success-bg border border-success-border rounded-xl flex items-center justify-between">
      <span className="text-success-green text-[13px] font-medium">✓ In Collection</span>
      <span className="text-success-green text-[13px] font-bold">
        {totalOwned} owned
      </span>
    </div>
  );

  const trackingRows = (
    <div className="space-y-2.5">
      {variants.length > 0 ? (
        variants.map((variant) => {
          const quantity = getVariantQuantity(card.id, variant);
          const config = getVariantConfig(variant);
          const override = tradeOverrides[overrideKey(card.id, variant)];
          const tradeableQty = getTradeableQty(quantity, override, tradeThreshold);
          const showTradeLine = quantity > 0 && (tradeableQty > 0 || override?.mode === 'exclude');
          const variantWishlisted = isWishlisted(card.id, variant);
          const variantWishlistItem = getWishlistItem(card.id, variant);

          return (
            <div key={variant} className="bg-cream-bg border border-border rounded-xl">
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`${config.color} text-white text-xs font-bold rounded-lg w-11 h-11 md:w-9 md:h-9 flex items-center justify-center flex-shrink-0`}
                  >
                    {config.short}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink-primary">{variant}</div>
                    {config.desc && (
                      <div className="text-[11px] text-ink-secondary truncate">{config.desc}</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleVariantWishlist(variant)}
                    title={variantWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                    className={`w-11 h-11 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors ${
                      variantWishlisted
                        ? 'bg-yellow text-navy-dark hover:bg-yellow/80'
                        : 'bg-white border border-border text-ink-faint hover:text-yellow-dark'
                    }`}
                  >
                    <Star size={14} fill={variantWishlisted ? 'currentColor' : 'none'} />
                  </button>
                  <span className="w-px h-6 bg-border" />
                  <button
                    onClick={() => decrementQuantity(variant)}
                    disabled={quantity === 0}
                    className={`w-11 h-11 md:w-8 md:h-8 rounded-full flex items-center justify-center ${
                      quantity === 0
                        ? 'bg-border text-ink-faint cursor-not-allowed'
                        : 'bg-danger-bg text-brick hover:bg-danger-border'
                    }`}
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-8 md:w-6 text-center font-heading font-bold text-ink-primary">{quantity}</span>
                  <button
                    onClick={() => incrementQuantity(variant)}
                    className="w-11 h-11 md:w-8 md:h-8 rounded-full flex items-center justify-center bg-success-bg text-success-green hover:bg-success-border"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {variantWishlisted && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-border/70">
                  <span className="text-[11px] font-medium text-yellow-dark">★ Wishlisted</span>
                  <div className="flex items-center gap-1.5">
                    {PRIORITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setWishlistPriority(variantWishlistItem, opt.key)}
                        className={`text-xs px-3 py-2 md:text-[10px] md:px-2 md:py-0.5 font-semibold rounded-full border transition-colors ${
                          variantWishlistItem?.priority === opt.key
                            ? 'bg-navy-dark text-white border-navy-dark'
                            : 'bg-white text-ink-secondary border-border hover:bg-cream-bg'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {showTradeLine && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-border/70">
                  {override?.mode === 'exclude' ? (
                    <>
                      <span className="text-[11px] text-ink-muted">Kept back from trading</span>
                      <button
                        onClick={() => clearTradeOverride(card.id, variant)}
                        className="text-[11px] font-semibold text-navy-dark hover:underline"
                      >
                        Offer again
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-[11px] font-medium text-success-green">
                        🔄 {tradeableQty} for trade
                      </span>
                      <button
                        onClick={() => setTradeOverride(card.id, variant, 'exclude')}
                        className="text-[11px] font-semibold text-ink-secondary hover:text-brick"
                      >
                        Don't trade
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div className="p-3 bg-cream-bg border border-border rounded-xl text-ink-secondary text-sm">
          No variant information available for this card.
        </div>
      )}
    </div>
  );

  const actionButtons = (
    <div>
      <button
        onClick={handleOfferTrade}
        className={`w-full font-semibold py-3 rounded-xl text-sm transition-colors ${
          firstTradeableVariant
            ? 'bg-navy-dark text-yellow hover:bg-navy-light'
            : 'bg-white border border-border text-ink-faint'
        }`}
      >
        Offer Trade
      </button>
      {comingSoonMsg && (
        <p className="text-center text-[12px] text-ink-muted mt-2">{comingSoonMsg}</p>
      )}

      {binderEligible && binderUI === 'idle' && (
        isBinderCard ? (
          <div className="mt-2 px-3 py-2.5 bg-success-bg border border-success-border rounded-xl text-center">
            <span className="text-success-green text-[13px] font-medium">
              ✓ In your binder — Slot {padDex(dexNumber)}
            </span>
          </div>
        ) : (
          <button
            onClick={handleAddToPokedex}
            className="mt-2 w-full py-3 rounded-xl text-sm font-bold bg-[#fef9ec] border-[1.5px] border-yellow text-navy-dark hover:bg-yellow/10 transition-colors flex items-center justify-center gap-1.5"
          >
            <BookOpen size={15} />
            Add to Pokédex
          </button>
        )
      )}

      {binderEligible && binderUI === 'confirm' && (
        <div className="mt-2 px-3 py-2.5 bg-cream-bg border border-border rounded-xl">
          <p className="text-[13px] text-ink-primary mb-2.5">
            This will replace <span className="font-bold">{existingSlot?.cardName}</span> in
            slot {padDex(dexNumber)}. Continue?
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleConfirmReplace}
              className="flex-1 py-2 rounded-lg text-[13px] font-bold bg-navy-dark text-yellow hover:bg-navy-light transition-colors"
            >
              Replace
            </button>
            <button
              onClick={() => setBinderUI('idle')}
              className="flex-1 py-2 rounded-lg text-[13px] font-semibold bg-white border border-border text-ink-secondary hover:bg-cream-bg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {binderEligible && binderUI === 'added' && (
        <div className="mt-2 px-3 py-2.5 bg-success-bg border border-success-border rounded-xl">
          <div className="flex items-center gap-2">
            <span className="text-success-green text-[15px]">✓</span>
            <div>
              <div className="text-[13px] font-bold text-success-green">Added to Pokédex</div>
              <div className="text-[11px] text-success-green/80">
                Slot {padDex(dexNumber)} — {NATIONAL_DEX[dexNumber]}
              </div>
            </div>
          </div>
          <button
            onClick={() => setBinderUI('idle')}
            className="mt-2 w-full py-2 rounded-lg text-[12px] font-semibold bg-white border border-border text-ink-secondary hover:bg-cream-bg transition-colors"
          >
            Change binder card →
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile: full-screen sheet */}
      <div className="md:hidden fixed inset-0 bg-cream-bg z-[9999] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white sticky top-0 z-10">
          <button onClick={onClose} className="flex items-center gap-1 text-ink-secondary text-sm">
            <ChevronLeft size={18} />
            Back
          </button>
          <span className="text-[12px] text-ink-muted">{displayNumber}</span>
        </div>

        <div className="px-4 pt-4 pb-8">
          <div className="h-[220px] rounded-2xl bg-gradient-to-br from-cream-card to-cream-bg shadow-card flex items-center justify-center overflow-hidden mb-4">
            <img
              src={card.images.large}
              alt={card.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => handleImgError(e, card)}
            />
          </div>

          <h2 className="font-heading font-bold text-xl text-ink-primary mb-2">{card.name}</h2>
          <div className="mb-4">{metaChips}</div>

          {ownedBadge && <div className="mb-4">{ownedBadge}</div>}

          <h3 className="text-sm font-semibold text-ink-primary mb-1">Collection Tracking</h3>
          <p className="text-[12px] text-ink-secondary mb-3">
            {variants.length} version{variants.length === 1 ? '' : 's'} available for this card
          </p>
          {trackingRows}

          <div className="mt-6">{actionButtons}</div>
        </div>
      </div>

      {/* Desktop: modal overlay */}
      <div
        className="hidden md:flex fixed inset-0 bg-black/50 items-center justify-center p-6 z-[9999]"
        onClick={handleBackdropClick}
      >
        <div
          className="bg-white rounded-2xl shadow-modal max-w-3xl w-full max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-6 pt-5">
            <div>
              <h2 className="font-heading font-bold text-2xl text-ink-primary">{card.name}</h2>
              <div className="text-ink-secondary text-sm mt-0.5">
                {displayNumber} • {card.rarity}
              </div>
            </div>
            <button onClick={onClose} className="text-ink-faint hover:text-ink-primary p-1">
              <X size={22} />
            </button>
          </div>

          <div className="flex gap-6 px-6 py-5">
            <div className="w-[280px] flex-shrink-0">
              <div className="rounded-2xl bg-gradient-to-br from-cream-card to-cream-bg shadow-card overflow-hidden mb-3">
                <img
                  src={card.images.large}
                  alt={card.name}
                  className="w-full h-auto object-contain"
                  onError={(e) => handleImgError(e, card)}
                />
              </div>
              {metaChips}
              {ownedBadge && <div className="mt-3">{ownedBadge}</div>}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-ink-primary mb-1">Collection Tracking</h3>
              <p className="text-[12px] text-ink-secondary mb-3">
                {variants.length} version{variants.length === 1 ? '' : 's'} available for this card
              </p>
              {trackingRows}

              <div className="mt-6">{actionButtons}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CardModal;
